import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { ReportConsole } from '@features/reports';
import { useOrganizationId } from '@features/user';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useEventTracking } from '@services/statsig';

import { useGetSemanticComponentQuery } from '../store/api';

import {
  drilldownBaseFilters,
  loopFromPayload,
  optimizationContextFromPayload,
} from './detailAdapters';
import { MetricLoopCard } from './MetricLoopCard';
import { OptimizationModal } from './OptimizationModal';

const BackLink: React.FC = () => (
  <Box
    component={RouterLink}
    to="/metrics"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.5,
      color: 'text.secondary',
      textDecoration: 'none',
      fontSize: 14,
      '&:hover': { color: 'text.primary' },
    }}
  >
    <ArrowBackIcon sx={{ fontSize: 16 }} />
    All metrics
  </Box>
);

/**
 * The metric drill-in: the loop chart (the EKG) + the lever, over the
 * reports-engine findings console (the microscope) pre-scoped to this metric
 * by the tile's drilldown. The console reuses the standalone reports table,
 * column-picker, filters and export — embedded and chrome-free.
 */
export const SemanticMetricDetailPage: React.FC = () => {
  const { practiceMetrics } = useEventTracking();
  const organizationId = useOrganizationId() ?? undefined;
  const { slug = '' } = useParams<{ slug: string }>();
  const { data: component, isLoading, isError } = useGetSemanticComponentQuery(
    { slug },
    { skip: !slug },
  );
  const [leverOpen, setLeverOpen] = useState(false);

  const payload = component?.data ?? undefined;
  const baseFilters = useMemo(
    () => drilldownBaseFilters(payload?.drilldown),
    [payload?.drilldown],
  );
  const loopData = useMemo(
    () => (payload ? loopFromPayload(payload) : null),
    [payload],
  );

  // Impression: fire once the metric's detail actually resolves.
  const openedRef = useRef(false);
  useEffect(() => {
    if (!payload || openedRef.current) return;
    openedRef.current = true;
    practiceMetrics.detailOpened({
      organization_id: organizationId,
      metric_slug: payload.slug,
      value_pct: payload.value_pct ?? null,
      meets_gate: payload.meets_gate,
      has_opportunity: !!payload.opportunity,
    });
  }, [payload, organizationId, practiceMetrics]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (isError || !payload) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <BackLink />
        <Alert severity="error" sx={{ mt: 2 }}>
          Couldn&apos;t load this metric.
        </Alert>
      </Container>
    );
  }

  const drilldown = payload.drilldown;
  const showLoop =
    payload.measure === 'ratio' && loopData != null && loopData.series.length > 0;

  return (
    <Box sx={{ bgcolor: '#faf9fc', minHeight: '100%', py: 4 }}>
      <Container maxWidth="lg">
        <BackLink />
        <Stack
          direction="row"
          sx={{
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            gap: 2,
            mt: 1.5,
            mb: 2.5,
          }}
        >
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {payload.display_name ?? payload.slug}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
              {[payload.applies_to_label, payload.measure, payload.grader_kind]
                .filter(Boolean)
                .join(' · ')}
            </Typography>
          </Box>
        </Stack>

        <Stack spacing={2.5}>
          {showLoop && loopData && <MetricLoopCard data={loopData} />}

          {payload.lever?.label && (
            <Box
              sx={{
                border: '1px solid',
                borderColor: t => alpha(t.palette.warning.main, 0.35),
                bgcolor: t => alpha(t.palette.warning.main, 0.08),
                borderRadius: 2,
                p: 2,
              }}
            >
              <Stack
                direction="row"
                sx={{
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 1.5,
                  flexWrap: 'wrap',
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      color: 'warning.dark',
                    }}
                  >
                    How we&apos;d help
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.primary', mt: 0.25 }}>
                    {payload.lever.label}
                  </Typography>
                </Box>
                {/* Active CTA only on opportunity metrics — matches the tile,
                    which passes onImprove only when an opportunity exists. The
                    lever ("How we'd help") still shows as context regardless. */}
                {payload.opportunity && (
                  <Button
                    variant="contained"
                    color="warning"
                    size="small"
                    onClick={() => {
                      practiceMetrics.improveClicked({
                        organization_id: organizationId,
                        metric_slug: payload.slug,
                        surface: 'detail',
                        value_pct: payload.value_pct ?? null,
                        target: payload.opportunity?.target ?? null,
                        gap: payload.opportunity?.gap ?? null,
                      });
                      setLeverOpen(true);
                    }}
                    sx={{ flexShrink: 0 }}
                  >
                    Improve this ↗
                  </Button>
                )}
              </Stack>
            </Box>
          )}

          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              Findings
            </Typography>
            {drilldown ? (
              <Box
                sx={{
                  height: 560,
                  border: '1px solid',
                  borderColor: 'grey.200',
                  borderRadius: 2.5,
                  overflow: 'hidden',
                  bgcolor: 'background.paper',
                }}
              >
                <ReportConsole
                  reportKey={drilldown.report_key}
                  baseFilters={baseFilters}
                  initialGroupBy={drilldown.group_by}
                  embedded
                />
              </Box>
            ) : (
              <Alert severity="info">
                This metric doesn&apos;t have a findings drill-in yet.
              </Alert>
            )}
          </Box>
        </Stack>
      </Container>

      <OptimizationModal
        open={leverOpen}
        onClose={reachedBooking => {
          practiceMetrics.optimizationClosed({
            organization_id: organizationId,
            metric_slug: payload.slug,
            surface: 'detail',
            reached_booking: reachedBooking,
          });
          setLeverOpen(false);
        }}
        onBook={() => {
          practiceMetrics.optimizationBookingClicked({
            organization_id: organizationId,
            metric_slug: payload.slug,
            surface: 'detail',
            target: payload.opportunity?.target ?? null,
            gap: payload.opportunity?.gap ?? null,
          });
        }}
        metric={optimizationContextFromPayload(payload)}
      />
    </Box>
  );
};

export default SemanticMetricDetailPage;
